import { useCallback, useEffect, useRef, useState } from 'react';

import { getStorage } from '@/shared/utils/general.util';
import { logger } from '@/shared/utils/logger';
import {
  applyPaymentRules,
  createEmptyDraft,
  type DraftIssuer,
  type InvoiceDraft,
} from '../lib/draft';
import { withoutExpiredBackdate } from '../lib/emissionDate';

const STORAGE_KEY = 'heru_invoice_v2_draft';
const LAST_INVOICE_KEY = 'heru_invoice_v2_last';
const AUTOSAVE_DEBOUNCE_MS = 800;

export type SaveState = 'idle' | 'saving' | 'saved';

/**
 * Un solo borrador activo a la vez, persistido fuera de React para que el flujo
 * sobreviva un refresh y la entrada directa a cualquiera de sus rutas.
 *
 * Hoy persiste en storage del navegador. El handoff pide persistencia en
 * servidor para que el borrador cruce dispositivos; cuando exista el endpoint,
 * se cambia `readDraft`/`writeDraft` y nada más de este archivo se mueve.
 */
export const readDraft = (): InvoiceDraft | null => {
  try {
    const raw = getStorage().getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as InvoiceDraft) : null;
  } catch (error) {
    logger.warn('No se pudo leer el borrador de factura', error);
    return null;
  }
};

const writeDraft = (draft: InvoiceDraft) => {
  try {
    getStorage().setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch (error) {
    logger.warn('No se pudo guardar el borrador de factura', error);
  }
};

export const discardDraft = () => {
  try {
    getStorage().removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn('No se pudo descartar el borrador de factura', error);
  }
};

/**
 * La última factura timbrada, para «Repetir la última factura». Se guarda con
 * el RFC del emisor porque el storage es del navegador, no de la cuenta: sin
 * esa marca, otra sesión en el mismo equipo vería los clientes y montos de
 * alguien más.
 */
export const saveLastInvoice = (draft: InvoiceDraft) => {
  if (!draft.issuer?.rfc) return;
  try {
    getStorage().setItem(
      LAST_INVOICE_KEY,
      JSON.stringify({ issuerRfc: draft.issuer.rfc, draft }),
    );
  } catch (error) {
    logger.warn('No se pudo guardar la última factura', error);
  }
};

export const readLastInvoice = (issuerRfc?: string): InvoiceDraft | null => {
  if (!issuerRfc) return null;
  try {
    const raw = getStorage().getItem(LAST_INVOICE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { issuerRfc: string; draft: InvoiceDraft };
    return saved.issuerRfc === issuerRfc ? saved.draft : null;
  } catch (error) {
    logger.warn('No se pudo leer la última factura', error);
    return null;
  }
};

const sameIssuer = (a: DraftIssuer | null, b: DraftIssuer): boolean =>
  !!a &&
  a.rfc === b.rfc &&
  a.name === b.name &&
  a.taxRegime === b.taxRegime &&
  a.postalCode === b.postalCode;

/**
 * El borrador guardado, corregido para que la pantalla no enseñe algo que ya no
 * se puede timbrar: uno de antes de la regla de PPD puede traer 03 con PPD, y una
 * fecha retroactiva caduca a las 72 h. Devuelve el id del borrador cuya fecha
 * regresó a hoy, para avisarlo en una línea.
 */
const restoreDraft = (issuer: DraftIssuer | null) => {
  const saved = readDraft();
  if (!saved) return { draft: createEmptyDraft(issuer), backdateResetDraftId: null };
  const { draft, wasReset } = withoutExpiredBackdate(applyPaymentRules(saved));
  return { draft, backdateResetDraftId: wasReset ? draft.id : null };
};

export const useInvoiceDraft = (issuer: DraftIssuer | null) => {
  const [restored] = useState(() => restoreDraft(issuer));
  const [draft, setDraft] = useState<InvoiceDraft>(restored.draft);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  /**
   * El emisor lo manda el perfil, no el borrador: se re-sincroniza cuando
   * CUALQUIER campo cambia, no solo el RFC.
   *
   * Comparar únicamente el RFC dejaba un borrador guardado con el código postal
   * vacío para siempre: los datos del emisor llegan en varias tandas (el
   * certificado legacy primero, el perfil fiscal después), y con el mismo RFC
   * en las dos el borrador se quedaba con la primera, incompleta, y el paso 1
   * pedía un CP que el perfil ya tenía.
   */
  useEffect(() => {
    if (!issuer) return;
    setDraft((current) => (sameIssuer(current.issuer, issuer) ? current : { ...current, issuer }));
  }, [issuer]);

  const update = useCallback((patch: Partial<InvoiceDraft> | ((current: InvoiceDraft) => Partial<InvoiceDraft>)) => {
    setSaveState('saving');
    setDraft((current) => {
      const resolved = typeof patch === 'function' ? patch(current) : patch;
      // Todo cambio al borrador pasa por aquí, así que las reglas del pago se
      // sostienen sin importar qué pantalla movió el método.
      return applyPaymentRules(
        { ...current, ...resolved, updatedAt: new Date().toISOString() },
        current,
      );
    });
  }, []);

  const reset = useCallback(() => {
    discardDraft();
    setDraft(createEmptyDraft(issuer));
    setSaveState('saved');
  }, [issuer]);

  /** Sustituye el borrador completo, sin mezclarlo con el actual. */
  const replace = useCallback((next: InvoiceDraft) => {
    setSaveState('saving');
    setDraft(applyPaymentRules(next));
  }, []);

  useEffect(() => {
    if (saveState !== 'saving') return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      writeDraft(draft);
      setSaveState('saved');
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [draft, saveState]);

  return {
    draft,
    update,
    reset,
    replace,
    saveState,
    backdateResetDraftId: restored.backdateResetDraftId,
  };
};
